import { DATA_SETS_ENUM_TYPES, MILLION } from 'constants/dataSets';
import {
  ADDRESS_BUILDING,
  ADDRESS_CITY,
  ADDRESS_COORDINATES,
  ADDRESS_COUNTRY,
  ADDRESS_POST_CODE,
  ADDRESS_STATE,
  ADDRESS_STREET,
  ASSET_CATEGORY,
  DATE_CLOSED,
  DATE_OPENED,
  END_DATE,
  EQUITY_SHAREHOLDING,
  EU_TAXONOMY_CODE,
  FINANCIAL_CONTROL,
  LEGAL_NAME,
  NACE_CODE,
  NACE_SECTION,
  NAME,
  NET_BOOK, NET_BOOK_CURRENCY,
  NUMBER_OF_EMPLOYEES,
  OWNED_LEASED,
  OWNERSHIP,
  PROFIT_CENTER_IDS,
  START_DATE,
  UNIQUE_IDENTIFIER,
  VALUE_CHAIN_LIST,
  VALUE_CHAIN_SECTION,
} from 'constants/formFields';
import { DATA_SETS_DATE_FORMAT, VC_BOARD_TYPES } from 'constants/general';
import dayjs from 'dayjs';

import createEnumOptions from '../createEnumOptions';
import getFormValue from '../getFormValue';
import getNaceLabel from '../getNaceLabel';

import type IOwnItem from '../../store/slices/dataSets/interfaces/IOwnItem';
import type { IGetOwnOperationsDefaultValues } from './interfaces/IGetOwnOperationsDefaultValues';

export const combineProfitCenters = (values?: IOwnItem, selectedProfitCenterId?: string) => {
  if (!values) return undefined;

  const { profitCenterIds = [], ...rest } = values;
  const updatedProfitCenterId = selectedProfitCenterId ? [...profitCenterIds, selectedProfitCenterId] : profitCenterIds;
  return { ...rest, profitCenterIds: updatedProfitCenterId };
};

function getOwnOperationsDefaultValues({
  values, naceCodeTreeData, enumData, companyCurrency, locationsCountries, valueChainSectionCurrent,
}: IGetOwnOperationsDefaultValues) {
  const {
    name, uniqueIdentifier, legalName, valueChainSection, ownership, equityShareholding,
    financialControl, assetCategory, ownedLeased, numberOfEmployees, dateOpened, dateClosed,
    naceSection, naceCode, euTaxonomyCode, netBookValue, addressCountry, addressState, addressPostCode, startDate,
    addressCity, addressStreet, addressBuilding, addressCoordinates, profitCenterIds, endDate,
  } = values || {};

  const defaultOption = undefined;
  const { naceSectionLabel, naceDvisions, naceCodeLabel } = getNaceLabel(
    { naceCode: naceCode || '', naceSection, naceCodeTreeData },
  );

  const assetCategoryOptions = createEnumOptions({
    enumData,
    values: assetCategory || [],
    enumKey: DATA_SETS_ENUM_TYPES.assetCategory,
  });

  const findProfitCenters = profitCenterIds?.length
    ? profitCenterIds.map((item) => ({ value: item, label: item }))
    : [];

  const getCurrencyBook = () => {
    if (netBookValue?.currency) {
      return netBookValue?.currency;
    }
    if (companyCurrency && (netBookValue?.currency === '' || !netBookValue?.currency)) {
      return companyCurrency;
    }

    return '';
  };

  const locationsCountriesOptions = (countryCode: string) => {
    const country = locationsCountries?.find((countryItem) => countryItem.iso2 === countryCode);
    return {
      value: country?.iso2 || countryCode,
      label: country?.name || countryCode,
    };
  };

  return {
    [NAME]: name || '',
    [UNIQUE_IDENTIFIER]: uniqueIdentifier || '',
    [LEGAL_NAME]: legalName || '',
    [PROFIT_CENTER_IDS]: findProfitCenters,
    [VALUE_CHAIN_SECTION]: {
      value: valueChainSectionCurrent || valueChainSection?.split('. ')[1],
      label: valueChainSectionCurrent || valueChainSection?.split('. ')[1],
    },
    [VALUE_CHAIN_LIST]: {
      value: VC_BOARD_TYPES.OWN_OPERATIONS,
      label: VC_BOARD_TYPES.OWN_OPERATIONS,
    },
    [OWNERSHIP]: ownership ? {
      value: ownership, label: ownership,
    } : defaultOption,
    [EQUITY_SHAREHOLDING]: getFormValue({ value: equityShareholding }),
    [FINANCIAL_CONTROL]: getFormValue({ value: financialControl }),
    [ASSET_CATEGORY]: assetCategoryOptions?.length > 0 ? assetCategoryOptions : [],
    [OWNED_LEASED]: ownedLeased ? {
      value: ownedLeased, label: ownedLeased,
    } : defaultOption,
    [NUMBER_OF_EMPLOYEES]: getFormValue({ value: numberOfEmployees }),
    [DATE_OPENED]: dateOpened ? dayjs(dateOpened).format(DATA_SETS_DATE_FORMAT) : '',
    [DATE_CLOSED]: dateClosed ? dayjs(dateClosed).format(DATA_SETS_DATE_FORMAT) : '',
    [START_DATE]: startDate ? dayjs(startDate).format(DATA_SETS_DATE_FORMAT) : '',
    [END_DATE]: endDate ? dayjs(endDate).format(DATA_SETS_DATE_FORMAT) : '',
    [NACE_SECTION]: naceSection ? {
      value: naceSection,
      label: naceSectionLabel || '',
      divisions: naceDvisions,
    } : defaultOption,
    [NACE_CODE]: naceCode ? {
      value: naceCode,
      label: naceCodeLabel || '',
    } : defaultOption,
    [EU_TAXONOMY_CODE]: euTaxonomyCode || '',
    [NET_BOOK]: netBookValue?.amount || netBookValue?.amount === 0
      ? getFormValue({ value: netBookValue.amount / MILLION }) : '',
    [NET_BOOK_CURRENCY]: getCurrencyBook(),
    [ADDRESS_COUNTRY]: addressCountry ? locationsCountriesOptions(addressCountry) : defaultOption,
    [ADDRESS_STATE]: addressState ? {
      value: addressState, label: addressState,
    } : defaultOption,
    [ADDRESS_POST_CODE]: addressPostCode || '',
    [ADDRESS_CITY]: addressCity ? {
      value: addressCity, label: addressCity,
    } : defaultOption,
    [ADDRESS_STREET]: addressStreet || '',
    [ADDRESS_BUILDING]: addressBuilding || '',
    [ADDRESS_COORDINATES]: addressCoordinates || '',
  };
}

export default getOwnOperationsDefaultValues;
